import { ChangeEvent, FC, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import { useRegistrationButtonState } from '@/hooks/registration/useRegistrationButtonState';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { RegistrationProvider } from '@/provider/registration/RegistrationProvider';
import { RegistrationLifecycleProvider } from '@/provider/registration/registrationLifecycleProvider';
import { useRegistrationLifecycleState } from '@/provider/registration/registrationLifecycleContext';
import { useAppContext } from '@/provider/contexts';
import { registerAPAndRTAPI } from '@/api/storeManagement/registration';
import { InfraDevice, RegisterAPAndRTRequest } from '@/types/pages/storeManagement/registration';
import {
    DEVICE_STATUS,
    REGISTRATION_MODE,
    TAB_TYPE,
} from '@/types/pages/storeManagement/registration/registrationTypes';
import { normalizeEslMac } from '@/utils/inputUtils';
import AddApDrawer from '../../../components/registration/addAp/AddApDrawer';
import AddTagDrawer from '../../../components/registration/addTag/AddTagDrawer';
import DeleteDialog from '@/components/registration/DeleteDialog';
import ToggleApLockDrawer from '@/components/registration/toggleApLock/ToggleApLockDrawer';
import DeviceListPanel from './component/DeviceListPanel';
import DeviceStepSwitcher from './component/DeviceStepSwitcher';
import RegisterDeviceFooter from './component/RegisterDeviceFooter';
import RegisterDeviceSkeleton from './component/RegisterDeviceSkeleton';
import RegistrationModeCard from './component/RegistrationModeCard';
import BaseIconButton from '@/components/ui/BaseIconButton';
import BaseFontIcon from '@/components/ui/BaseFontIcon';
import ScannerBaseTextField from '@/mobile/components/ScannerBaseTextField';
import { REGISTER_STEPS } from './constants';
import { RegisterFooterAction } from './type';

const normalizeSearchMac = (value: string) => {
    return normalizeEslMac(value.trim());
};

const searchInputSx = {
    bgcolor: '#FFFFFF',
    borderRadius: '12px',

    '& .MuiOutlinedInput-root': {
        height: 40,
        borderRadius: '12px',
        fontSize: 14,
        fontWeight: 700,

        '& fieldset': {
            borderColor: '#D6E4FF',
        },

        '&:hover fieldset': {
            borderColor: '#93C5FD',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#2563EB',
            borderWidth: 1,
        },
    },
};

const refreshButtonSx = {
    width: 40,
    height: 40,
    flexShrink: 0,
    border: '1px solid #D6E4FF',
    bgcolor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
    WebkitTapHighlightColor: 'transparent',

    '&:focus, &:focus-visible': {
        outline: 'none',
    },
};

const RegisterDevice: FC = () => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const { state, dispatch } = useRegistrationState();
    const buttonState = useRegistrationButtonState();
    const { refreshTable, refreshRegistrationNow } = useRegistrationLifecycleState();

    const [searchValue, setSearchValue] = useState(state.searchMac);
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setSearchValue(state.searchMac);
    }, [state.searchMac]);

    const selectedDevice = useMemo(() => {
        return state.deviceInfoState.selectedInfraDevices[0] || null;
    }, [state.deviceInfoState.selectedInfraDevices]);

    const activeStep = useMemo(() => {
        return REGISTER_STEPS.findIndex((step) => step.key === state.currentTab);
    }, [state.currentTab]);

    const isRegistrationBlocked = useMemo(() => {
        return state.registrationMode === REGISTRATION_MODE.NORMAL_MODE && !state.infraDevice.length;
    }, [state.registrationMode, state.infraDevice.length]);

    const lockedStepMap = useMemo<Record<TAB_TYPE, boolean>>(() => {
        const disabledMap = new Map(state.labelOptions.map((option) => [option.key, option.disable]));

        return {
            [TAB_TYPE.AP]: Boolean(disabledMap.get(TAB_TYPE.AP)),
            [TAB_TYPE.RT]: Boolean(disabledMap.get(TAB_TYPE.RT)),
            [TAB_TYPE.TAG]: Boolean(disabledMap.get(TAB_TYPE.TAG)),
            [TAB_TYPE.NONE]: true,
        };
    }, [state.labelOptions]);

    const footerAction = useMemo<RegisterFooterAction>(() => {
        if (state.currentTab === TAB_TYPE.AP) {
            if (selectedDevice) {
                return {
                    type: 'edit-ap',
                    label: 'Edit',
                    disabled: !buttonState.editAP,
                };
            }

            return {
                type: 'add-ap',
                label: 'Add AP',
                disabled: !buttonState.addAP,
            };
        }

        if (state.currentTab === TAB_TYPE.RT) {
            if (!selectedDevice) {
                return null;
            }

            if (selectedDevice.isAP) {
                return {
                    type: 'toggle-ap-lock',
                    label: selectedDevice.isLocked ? 'UNLOCK' : 'LOCK',
                };
            }

            if (selectedDevice.isRT && selectedDevice.confirmed === DEVICE_STATUS.UN_CONFIRMED) {
                return {
                    type: 'confirm-rt',
                    label: 'Confirm',
                    disabled: !buttonState.confirmRT,
                };
            }

            return null;
        }

        if (state.currentTab === TAB_TYPE.TAG && state.registrationMode === REGISTRATION_MODE.INSTALLATION_MODE) {
            return {
                type: 'add-tag',
                label: 'Add TAG',
                disabled: !buttonState.addTag,
            };
        }

        return null;
    }, [
        buttonState.addAP,
        buttonState.addTag,
        buttonState.confirmRT,
        buttonState.editAP,
        selectedDevice,
        state.currentTab,
        state.registrationMode,
    ]);

    const canSwitchStep = useCallback(
        (step: TAB_TYPE) => {
            return !lockedStepMap[step];
        },
        [lockedStepMap],
    );

    const handleStepChange = useCallback(
        (stepIndex: number) => {
            const step = REGISTER_STEPS[stepIndex];

            if (!step) return;
            if (step.key === state.currentTab) return;
            if (!canSwitchStep(step.key)) return;

            dispatch({
                type: 'SET_TAB',
                payload: step.key,
            });

            void refreshTable({
                tab: step.key,
                resetTable: true,
                clearSelected: true,
            });
        },
        [canSwitchStep, dispatch, refreshTable, state.currentTab],
    );

    const handlePageChange = useCallback(
        (nextPage: number) => {
            const nextPaginationModel = {
                ...state.deviceInfoState.paginationModel,
                page: nextPage,
            };

            dispatch({
                type: 'SET_TABLE_STATE',
                payload: {
                    paginationModel: nextPaginationModel,
                },
            });

            void refreshTable({
                tab: state.currentTab,
                paginationModel: nextPaginationModel,
                clearSelected: true,
            });
        },
        [dispatch, refreshTable, state.currentTab, state.deviceInfoState.paginationModel],
    );

    const handleRowsPerPageChange = useCallback(
        (nextRowsPerPage: number) => {
            const nextPaginationModel = {
                page: 0,
                pageSize: nextRowsPerPage,
            };

            dispatch({
                type: 'SET_TABLE_STATE',
                payload: {
                    paginationModel: nextPaginationModel,
                },
            });

            void refreshTable({
                tab: state.currentTab,
                paginationModel: nextPaginationModel,
                clearSelected: true,
            });
        },
        [dispatch, refreshTable, state.currentTab],
    );

    const commitSearch = useCallback(
        (value = searchValue) => {
            const normalizedValue = normalizeSearchMac(value);

            setSearchValue(normalizedValue);

            dispatch({
                type: 'SET_SEARCH_MAC',
                payload: normalizedValue,
            });

            void refreshTable({
                tab: state.currentTab,
                searchMac: normalizedValue,
                clearSelected: true,
            });

            searchRef.current?.blur();
        },
        [dispatch, refreshTable, searchValue, state.currentTab],
    );

    const handleSearchInputChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchValue(normalizeSearchMac(event.target.value));
    }, []);

    const handleSearchScannerValue = useCallback(
        (value: string) => {
            commitSearch(value);
        },
        [commitSearch],
    );

    const handleSearchKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;

            event.preventDefault();
            event.stopPropagation();

            commitSearch();
        },
        [commitSearch],
    );

    const handleClearSearch = useCallback(
        (event?: MouseEvent<HTMLButtonElement>) => {
            event?.preventDefault();
            event?.stopPropagation();

            setSearchValue('');

            dispatch({
                type: 'SET_SEARCH_MAC',
                payload: '',
            });

            void refreshTable({
                tab: state.currentTab,
                searchMac: '',
                clearSelected: true,
            });

            searchRef.current?.focus();
        },
        [dispatch, refreshTable, state.currentTab],
    );

    const confirmRTHandler = useCallback(
        async (mac: string) => {
            try {
                const postData: RegisterAPAndRTRequest = {
                    mac,
                    type: 'rt',
                    store_id: manageStoreId as number,
                };

                const {
                    data: { isSuccess, message },
                } = await registerAPAndRTAPI(postData);

                void refreshRegistrationNow({
                    clearSelected: true,
                }).finally(() => {
                    setNotification({
                        message,
                        severity: isSuccess ? 'success' : 'error',
                    });
                });
            } catch {
                setNotification({
                    message: 'Something went wrong',
                    severity: 'error',
                });
            }
        },
        [manageStoreId, refreshRegistrationNow, setNotification],
    );

    const handleActionClick = useCallback(async () => {
        if (!footerAction) return;

        switch (footerAction.type) {
            case 'add-ap':
                dispatch({
                    type: 'TOGGLE_DIALOG',
                    payload: {
                        dialog: 'isApOpen',
                        isOpen: true,
                    },
                });
                return;

            case 'edit-ap':
                if (!selectedDevice) return;

                dispatch({
                    type: 'SET_AP_INFO',
                    payload: selectedDevice as InfraDevice,
                });

                dispatch({
                    type: 'TOGGLE_DIALOG',
                    payload: {
                        dialog: 'isApOpen',
                        isOpen: true,
                    },
                });
                return;

            case 'toggle-ap-lock':
                if (!selectedDevice) return;

                dispatch({
                    type: 'SET_AP_LOCK_INFO',
                    payload: {
                        mac: selectedDevice.mac,
                        isLock: Boolean(selectedDevice.isLocked),
                    },
                });

                dispatch({
                    type: 'TOGGLE_DIALOG',
                    payload: {
                        dialog: 'isApLockOpen',
                        isOpen: true,
                    },
                });
                return;

            case 'confirm-rt':
                if (!selectedDevice) return;

                await confirmRTHandler(selectedDevice.mac);
                return;

            case 'add-tag':
                dispatch({
                    type: 'TOGGLE_DIALOG',
                    payload: {
                        dialog: 'isTagOpen',
                        isOpen: true,
                    },
                });
                return;

            default:
                return;
        }
    }, [confirmRTHandler, dispatch, footerAction, selectedDevice]);

    const handleRefresh = useCallback(() => {
        void refreshRegistrationNow({
            clearSelected: true,
        });
    }, [refreshRegistrationNow]);

    if (state.loadingState.storeStateLoading) {
        return <RegisterDeviceSkeleton />;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    bgcolor: '#F8FAFC',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                        px: 1.5,
                        py: 1,
                        gap: 1,
                    }}
                >
                    <RegistrationModeCard />

                    <DeviceStepSwitcher
                        activeStep={activeStep}
                        lockedStepMap={lockedStepMap}
                        canSwitchStep={canSwitchStep}
                        onStepChange={handleStepChange}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <ScannerBaseTextField
                            inputRef={searchRef}
                            value={searchValue}
                            size="small"
                            fullWidth
                            placeholder={t('Search by MAC')}
                            onScannerValue={handleSearchScannerValue}
                            onChange={handleSearchInputChange}
                            onKeyDown={handleSearchKeyDown}
                            slotProps={{
                                input: {
                                    endAdornment: searchValue ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                tabIndex={-1}
                                                aria-label={t('Clear')}
                                                edge="end"
                                                size="small"
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                }}
                                                onClick={handleClearSearch}
                                                sx={{
                                                    color: '#64748B',
                                                    WebkitTapHighlightColor: 'transparent',

                                                    '&:focus, &:focus-visible': {
                                                        outline: 'none',
                                                    },
                                                }}
                                            >
                                                <Close fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                },
                            }}
                            sx={searchInputSx}
                        />

                        <BaseIconButton
                            tabIndex={-1}
                            sx={refreshButtonSx}
                            color="secondary"
                            disabled={!buttonState.refresh || state.deviceInfoState.isLoading}
                            onMouseDown={(event) => {
                                event.preventDefault();
                            }}
                            onClick={handleRefresh}
                        >
                            <BaseFontIcon className="fa-regular fa-arrows-rotate" />
                        </BaseIconButton>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {isRegistrationBlocked ? (
                            <Box
                                sx={{
                                    height: '100%',
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 800,
                                        color: '#0F172A',
                                        mb: 0.75,
                                    }}
                                >
                                    {t('Installation Mode Required')}
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: '#64748B',
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {t('Please Start Installation Mode to do Registration.')}
                                </Typography>
                            </Box>
                        ) : (
                            <DeviceListPanel
                                key={`${state.registrationMode}-${state.currentTab}`}
                                activeTab={state.currentTab}
                            />
                        )}
                    </Box>
                </Box>

                {!isRegistrationBlocked && (
                    <RegisterDeviceFooter
                        count={state.deviceInfoState.rowCount || 0}
                        page={state.deviceInfoState.paginationModel.page}
                        rowsPerPage={state.deviceInfoState.paginationModel.pageSize}
                        action={footerAction}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onActionClick={handleActionClick}
                    />
                )}
            </Box>

            <ToggleApLockDrawer />
            <AddApDrawer />
            <AddTagDrawer />
            <DeleteDialog />
        </>
    );
};

const RegisterDeviceView: FC = () => {
    return (
        <RegistrationProvider>
            <RegistrationLifecycleProvider>
                <RegisterDevice />
            </RegistrationLifecycleProvider>
        </RegistrationProvider>
    );
};

export default RegisterDeviceView;
